import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigation } from "@refinedev/core";
import {
  Badge,
  Button,
  Dropdown,
  Flex,
  Input,
  Space,
  Table,
  Tooltip,
  Typography,
  message,
  type TableColumnsType,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ChannelsIcon } from "@/components";
import { Channel, conversationsApi } from "@/services/conversations.api";
import { estaRestringido, textoVencimiento } from "@/services/channel-restriction";
import { CHANNEL_TYPES, isCloudApi, labelFor } from "@/services/channel-types";
import { channelIcon } from "@/components/channel-picker";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";

const { Title, Text } = Typography;

/** Lo que ofrece el alta mientras no se sabe qué habilita el backend: el QR es lo que siempre supo crear. */
const SOLO_QR = new Set(["whatsapp"]);

/** Canales del **store seleccionado** (`/stores/channels/:id`, id = storeCode del SmartMenu). */
export const ChannelsPage = () => {
  const { id: storeCode = "" } = useParams();
  const { push } = useNavigation();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  /**
   * Los tipos que este ambiente puede dar de alta (D26). El dropdown se arma con lo que anuncia el
   * backend: un front nuevo contra un backend sin el endpoint, o sin la app de Meta configurada, no
   * ofrece un canal que no se podría conectar nunca.
   */
  const [enabledTypes, setEnabledTypes] = useState<Set<string>>(SOLO_QR);

  useEffect(() => {
    void loadChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeCode]);

  useEffect(() => {
    let active = true;
    conversationsApi
      .channelTypes()
      // Una lista vacía no la da un backend sano —el QR siempre está—: es una respuesta que no era JSON.
      .then((types) => active && setEnabledTypes(types.length ? new Set(types.map((t) => t.type)) : SOLO_QR))
      // Un fallo que no es el 404 de un backend viejo deja lo de siempre: sólo el QR.
      .catch(() => active && setEnabledTypes(SOLO_QR));
    return () => {
      active = false;
    };
  }, []);

  async function loadChannels() {
    if (!storeCode) return;
    setLoading(true);
    try {
      setChannels(await conversationsApi.listChannels(storeCode));
    } catch (e: any) {
      message.error(`No se pudieron cargar los canales: ${e?.response?.data?.message ?? e?.message ?? e}`);
    } finally {
      setLoading(false);
    }
  }

  async function handleAddChannel(type: string) {
    try {
      // El canal es relativo al store seleccionado (no se pide store).
      const channel = await conversationsApi.createChannel({ storeCode, type });
      // ?new=1 → el detalle arranca en modo creación (form editable, footer solo Save).
      push(`/stores/channels/${storeCode}/${channel.channelId}?new=1`);
    } catch (e: any) {
      message.error(`No se pudo crear el canal: ${e?.response?.data?.message ?? e?.message ?? e}`);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return channels;
    return channels.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.identifier.toLowerCase().includes(q) ||
        labelFor(c.type).toLowerCase().includes(q),
    );
  }, [channels, search]);

  const columns: TableColumnsType<Channel> = [
    {
      title: "Nombre",
      dataIndex: "name",
      key: "name",
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: "Tipo",
      dataIndex: "type",
      key: "type",
      width: 220,
      render: (t: string) => (
        <Flex align="center" gap={8}>
          {channelIcon(t)}
          {labelFor(t)}
        </Flex>
      ),
    },
    {
      title: "Canal",
      dataIndex: "identifier",
      key: "identifier",
      // El identifier arranca como placeholder (= channelId) hasta que se carga el número.
      render: (identifier: string, r) => (identifier === r.channelId ? <Text type="secondary">—</Text> : identifier),
    },
    {
      title: "Estado",
      dataIndex: "isActive",
      key: "status",
      width: 160,
      align: "right",
      // #177: una cuenta restringida por WhatsApp se ve acá, no sólo entrando al canal. Gana sobre
      // Activo/Inactivo porque es lo que explica por qué no salen mensajes; el detalle dice hasta
      // cuándo. Una restricción sin fecha también cuenta: ver services/channel-restriction.
      render: (isActive: boolean, r) =>
        estaRestringido(r) ? (
          <Tooltip
            title={`WhatsApp restringió la cuenta ${textoVencimiento(r.restrictedUntil)}.${
              isCloudApi(r.type) ? "" : " Re-escanear el QR no lo soluciona."
            }`}
          >
            <Badge status="error" text="Restringido" />
          </Tooltip>
        ) : isActive ? (
          <Badge status="success" text="Activo" />
        ) : (
          <Badge status="default" text="Inactivo" />
        ),
    },
    // Bug 17 (#109): la acción de eliminar vive solo en el detalle del canal, no en el listado.
  ];

  return (
    <div>
      <Space align="center" style={{ marginBottom: 20 }}>
        <ChannelsIcon style={{ fontSize: 22 }} />
        <Title level={3} style={{ margin: 0 }}>
          Canales
        </Title>
      </Space>

      <Flex gap={12} align="center" style={{ marginBottom: 12 }}>
        <Input.Search
          allowClear
          placeholder="Buscar por nombre, email o WhatsApp"
          onChange={(e) => setSearch(e.target.value)}
          onSearch={setSearch}
          style={{ flex: 1 }}
        />
        <Dropdown
          trigger={["click"]}
          menu={{
            items: CHANNEL_TYPES.map((t) => ({
              key: t.value,
              label: t.label,
              disabled: !enabledTypes.has(t.value),
            })),
            onClick: ({ key }) => void handleAddChannel(key),
          }}
        >
          <Button type="primary" iconPosition="end" icon={<PlusOutlined />}>
            Agregar canal
          </Button>
        </Dropdown>
      </Flex>

      <Table<Channel>
        rowKey="channelId"
        loading={loading}
        columns={columns}
        dataSource={filtered}
        onRow={(record) => ({
          onClick: () => push(`/stores/channels/${storeCode}/${record.channelId}`),
          style: { cursor: "pointer" },
        })}
        title={() => (
          <Text type="secondary">
            {filtered.length} {filtered.length === 1 ? "canal" : "canales"}
          </Text>
        )}
        pagination={{
          defaultPageSize: FILAS_POR_PAGINA,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          showTotal: (total) => `${total} ${total === 1 ? "canal" : "canales"}`,
          locale: {
            items_per_page: "/ página",
            jump_to: "Ir a",
            page: "página",
            prev_page: "Página anterior",
            next_page: "Página siguiente",
          },
        }}
      />
    </div>
  );
};

export default ChannelsPage;
export { ChannelDetail } from "./detail";
